// =============================================================================
// Orçamento do ano seguinte — a proposta da área (Painel do Gestor, 071)
//
// Item 2.2 do pedido do Financeiro de 20/10, no desenho fechado com o Enzo:
//   · vem preenchida, mês a mês, com o que a área gastou no ano base — os meses
//     fechados como foram, os que faltam pela média;
//   · o gestor muda o que precisar; mês acima do mesmo mês do ano base, ou o
//     ano acima, pede uma explicação por categoria (a tela diz quais meses);
//   · item novo (uma contratação) entra embaixo da categoria, com descrição;
//   · envia ao Financeiro. A linha aprovada trava; só a devolvida volta.
//
// Quem pode é decisão do banco (gestor da área ou Financeiro); a tela só não
// oferece o que a RPC vai recusar.
// =============================================================================

import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  abrirProposta, api, buscarAreasProposta, buscarProposta, enviarProposta, FalhaApi, salvarProposta,
  detalheDaLinha, type AreaProposta, type LinhaProposta, type LinhaPropostaEdicao,
} from "../lib/api.ts";
import { deCentavos, paraCentavos } from "../lib/dinheiro.ts";
import { moeda, percentual } from "../lib/formato.ts";
import {
  aPartirDe, COR_STATUS_AREA, editavel, MESES, mesesAcima, pedeExplicacao, ROTULO_SITUACAO, ROTULO_STATUS_AREA, soma,
  textoMesesAcima, variacao, zeros, type SituacaoLinha,
} from "../lib/proposta.ts";
import { Painel, Vazio } from "../componentes/ui.tsx";
import { CaixaGrade, CLASSE_BOTAO_BARRA } from "../componentes/Grade.tsx";
import { Modal } from "../componentes/Modal.tsx";
import { baixarCsv } from "../lib/csv.ts";
import { decodificarArquivo, detectarSeparador, dividir } from "../lib/planilha.ts";
import { compararImportacao, lerPlanilhaProposta, montarPlanilhaProposta, type Comparacao } from "../lib/propostaPlanilha.ts";

type Cadastros = { categorias: { id: string; codigo: string | null; nome: string; natureza: string }[] };

/** O que está na tela: a linha do servidor com o que o gestor mudou, ou uma linha nova. */
type Linha = {
  chave: string;
  id?: string;
  categoria_id: string;
  categoria_nome: string;
  categoria_codigo: string | null;
  descricao: string | null;
  /** A pessoa ou a subcategoria (082); nula na linha da categoria e no item novo. */
  detalhe: string | null;
  /** Pessoa ou subcategoria: só existe porque veio do ano base. */
  pessoa: boolean;
  base: string[];
  pedido: string[];
  aprovado: string[] | null;
  observacao: string;
  situacao: SituacaoLinha;
  justificativa: string | null;
};

const daServidor = (l: LinhaProposta): Linha => ({
  chave: l.id, id: l.id, categoria_id: l.categoria_id, categoria_nome: l.categoria_nome, categoria_codigo: l.categoria_codigo,
  descricao: l.descricao, detalhe: detalheDaLinha(l), pessoa: !!l.fornecedor_id,
  base: l.base_centavos, pedido: l.pedido_centavos, aprovado: l.aprovado_centavos,
  observacao: l.observacao ?? "", situacao: l.situacao, justificativa: l.justificativa,
});

/** A categoria somada: a linha dela e os detalhes, sem os itens novos — a mesma conta do banco (082). */
function somaCategoria(categoria: Linha | null, detalhes: Linha[]): { base: string[]; pedido: string[] } {
  const ls = [...(categoria ? [categoria] : []), ...detalhes];
  const mes = (k: "base" | "pedido", i: number) => ls.reduce((t, l) => t + BigInt(l[k][i] || "0"), 0n).toString();
  return { base: MESES.map((_, i) => mes("base", i)), pedido: MESES.map((_, i) => mes("pedido", i)) };
}

export function PropostaGestor() {
  const qc = useQueryClient();
  const areasQ = useQuery({ queryKey: ["propostas-areas"], queryFn: buscarAreasProposta });
  const ano = useMemo(() => Math.max(0, ...(areasQ.data?.areas ?? []).map((a) => a.exercicio_ano)), [areasQ.data]);
  const minhas = useMemo(() => (areasQ.data?.areas ?? []).filter((a) => a.exercicio_ano === ano), [areasQ.data, ano]);
  const [ccId, setCcId] = useState<string | null>(null);
  const area = minhas.find((a) => a.centro_custo_id === ccId) ?? minhas[0];

  const abrir = useMutation({
    mutationFn: () => abrirProposta(area!.centro_custo_id, ano),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["propostas-areas"] }),
  });

  if (areasQ.isPending) return <div className="pagina py-6 text-[13px] text-tinta-fraca">Carregando…</div>;
  if (areasQ.isError) return <div className="pagina py-6"><Vazio titulo="Não foi possível carregar" dica={(areasQ.error as FalhaApi).message} /></div>;
  if (!area) {
    return (
      <div className="pagina py-6">
        <Vazio titulo="Nenhum orçamento em montagem"
          dica="Quando o Financeiro abrir o orçamento do próximo ano, a proposta da sua área aparece aqui." />
      </div>
    );
  }

  return (
    <div className="pagina pb-20">
      <header className="flex flex-wrap items-end justify-between gap-3 pt-7 pb-4">
        <div>
          <p className="text-[11px] font-semibold tracking-[0.11em] text-tinta-fraca uppercase">Orçamento {ano}</p>
          <h1 className="mt-1 font-display text-[22px] leading-tight font-bold tracking-tight">A proposta da área</h1>
          <p className="mt-1 max-w-3xl text-[14px] text-tinta-fraca">
            Já vem com o que a área gastou em {ano - 1}: os meses fechados como foram, e os que faltam pela média. Mude o que
            precisar. O que ficar acima de {ano - 1}, em algum mês ou no ano, pede uma explicação. Depois, envie ao Financeiro.
          </p>
        </div>
        {minhas.length > 1 && (
          <label className="text-[12px] font-semibold text-tinta-media">Área
            <select value={area.centro_custo_id} onChange={(e) => setCcId(e.target.value)}
              className="mt-1 block rounded-lg border border-linha bg-superficie px-3 py-1.5 text-[13px]">
              {minhas.map((a) => <option key={a.centro_custo_id} value={a.centro_custo_id}>{a.centro_custo_codigo} · {a.centro_custo_nome}</option>)}
            </select>
          </label>
        )}
      </header>

      {!area.proposta_id ? (
        <Painel>
          <Vazio titulo={`O orçamento de ${ano} de ${area.centro_custo_codigo} · ${area.centro_custo_nome} ainda não foi começado`}
            dica={`Ao começar, a proposta vem preenchida com o gasto de ${ano - 1}, categoria por categoria.`} />
          <div className="mt-4 flex justify-center">
            <button type="button" disabled={abrir.isPending} onClick={() => abrir.mutate()}
              className="rounded-lg bg-laranja px-4 py-2 text-[13px] font-bold text-azul disabled:opacity-45">
              {abrir.isPending ? "Preparando…" : `Montar o orçamento de ${ano}`}
            </button>
          </div>
          {abrir.isError && <p className="mt-2 text-center text-[12.5px] text-risco-tinta">{(abrir.error as FalhaApi).message}</p>}
        </Painel>
      ) : (
        <Editor key={area.proposta_id} area={area} ano={ano} />
      )}
    </div>
  );
}

function Editor({ area, ano }: { area: AreaProposta; ano: number }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["proposta", area.proposta_id], queryFn: () => buscarProposta(area.proposta_id!) });
  const cad = useQuery({ queryKey: ["cadastros"], queryFn: () => api.get<Cadastros>("/api/cadastros") });
  const [linhas, setLinhas] = useState<Linha[]>([]);
  const [removidas, setRemovidas] = useState<string[]>([]);
  const [mudou, setMudou] = useState(false);
  const [aviso, setAviso] = useState<string | null>(null);

  // O servidor é a verdade a cada carga; o que não foi salvo some ao recarregar.
  useEffect(() => {
    if (q.data) { setLinhas(q.data.linhas.map(daServidor)); setRemovidas([]); setMudou(false); }
  }, [q.data]);

  const atualizar = (chave: string, mudanca: Partial<Linha>) => {
    setLinhas((ls) => ls.map((l) => (l.chave === chave ? { ...l, ...mudanca } : l)));
    setMudou(true);
  };
  const recarregar = () => {
    void qc.invalidateQueries({ queryKey: ["proposta", area.proposta_id] });
    void qc.invalidateQueries({ queryKey: ["propostas-areas"] });
  };

  // O que vai para o banco: só o que o gestor ainda pode mexer.
  const lote = (): LinhaPropostaEdicao[] => [
    ...removidas.map((id) => ({ id, remover: true, pedido_centavos: zeros() })),
    ...linhas.filter((l) => editavel(l.situacao)).map((l) => ({
      ...(l.id ? { id: l.id } : { categoria_id: l.categoria_id }),
      descricao: l.descricao, pedido_centavos: l.pedido, observacao: l.observacao || null,
    })),
  ];
  const salvar = useMutation({
    mutationFn: async () => { const lt = lote(); if (lt.length) await salvarProposta(area.proposta_id!, lt); },
    onSuccess: () => { setAviso("Rascunho salvo."); recarregar(); },
  });
  const enviar = useMutation({
    mutationFn: async () => {
      const lt = lote();
      if (lt.length) await salvarProposta(area.proposta_id!, lt);
      return enviarProposta(area.proposta_id!);
    },
    onSuccess: (r) => { setAviso(r.enviadas === 1 ? "1 linha enviada ao Financeiro." : `${r.enviadas} linhas enviadas ao Financeiro.`); recarregar(); },
  });
  const erro = (salvar.error ?? enviar.error) as FalhaApi | null;
  // Até a proposta voltar do servidor, a tela ainda mostra o que acabou de ser enviado.
  const ocupado = salvar.isPending || enviar.isPending || q.isFetching;

  const grupos = useMemo(() => {
    const m = new Map<string, { nome: string; codigo: string | null; categoria: Linha | null; detalhes: Linha[]; itens: Linha[] }>();
    for (const l of linhas) {
      const g = m.get(l.categoria_id) ?? { nome: l.categoria_nome, codigo: l.categoria_codigo, categoria: null, detalhes: [], itens: [] };
      if (l.descricao !== null) g.itens.push(l);
      else if (l.detalhe !== null) g.detalhes.push(l);
      else g.categoria = l;
      m.set(l.categoria_id, g);
    }
    for (const g of m.values()) g.detalhes.sort((a, b) => (a.detalhe ?? "").localeCompare(b.detalhe ?? ""));
    return [...m.entries()].sort((a, b) => (a[1].codigo ?? a[1].nome).localeCompare(b[1].codigo ?? b[1].nome, undefined, { numeric: true }));
  }, [linhas]);

  const totalBase = soma(linhas.map((l) => l.base).flat());
  const totalPedido = soma(linhas.map((l) => l.pedido).flat());
  const faltaExplicar = grupos.flatMap(([, g]) => {
    const t = somaCategoria(g.categoria, g.detalhes);
    const enviando = [g.categoria, ...g.detalhes].some((l) => l && editavel(l.situacao));
    return enviando && g.categoria && pedeExplicacao(t.base, t.pedido) && !g.categoria.observacao.trim() ? [g.categoria] : [];
  });
  const algoParaEnviar = linhas.some((l) => editavel(l.situacao));
  const presentes = new Set(linhas.filter((l) => l.descricao === null && l.detalhe === null).map((l) => l.categoria_id));
  // Só as categorias que esta área orça (081): a do Financeiro e a de outras
  // áreas não aparecem para acrescentar nem são aceitas na importação.
  const daArea = q.data?.categorias_da_area ? new Set(q.data.categorias_da_area) : null;
  const daAreaOk = (id: string) => !daArea || daArea.has(id);
  const opcoes = (cad.data?.categorias ?? []).filter((c) => c.natureza !== "receita" && !presentes.has(c.id) && daAreaOk(c.id))
    .sort((a, b) => (a.codigo ?? a.nome).localeCompare(b.codigo ?? b.nome, undefined, { numeric: true }));

  const novaLinha = (categoria_id: string, descricao: string | null, pedido: string[]) => {
    const c = cad.data?.categorias.find((x) => x.id === categoria_id);
    const grupo = linhas.find((l) => l.categoria_id === categoria_id);
    setLinhas((ls) => [...ls, {
      chave: `nova-${Date.now()}-${Math.random()}`, categoria_id, descricao, detalhe: null, pessoa: false,
      categoria_nome: grupo?.categoria_nome ?? c?.nome ?? "", categoria_codigo: grupo?.categoria_codigo ?? c?.codigo ?? null,
      base: zeros(), pedido, aprovado: null, observacao: "", situacao: "rascunho", justificativa: null,
    }]);
    setMudou(true);
  };
  const tirar = (l: Linha) => {
    setLinhas((ls) => ls.filter((x) => x.chave !== l.chave));
    if (l.id) setRemovidas((r) => [...r, l.id!]);
    setMudou(true);
  };

  // A proposta em planilha (item 2.2: "com opção de importar planilha"). Baixar
  // leva a proposta como está na tela; importar só mostra o que muda — quem
  // grava é o "Salvar rascunho", depois de o gestor conferir.
  const entrada = useRef<HTMLInputElement>(null);
  const [importacao, setImportacao] = useState<{ arquivo: string; erros: string[]; comparacao: Comparacao<Linha> } | null>(null);
  const [lendo, setLendo] = useState(false);
  const baixar = () => baixarCsv(`proposta-${area.centro_custo_codigo}-${ano}.csv`, montarPlanilhaProposta(linhas, ano));
  const importar = async (arquivo: File) => {
    setLendo(true);
    try {
      let tabela: unknown[][];
      if (/\.xlsx$/i.test(arquivo.name)) {
        // O leitor só carrega quando alguém escolhe um .xlsx.
        const { default: lerExcel } = await import("read-excel-file/browser");
        const abas = (await lerExcel(arquivo)) as { sheet: string; data: unknown[][] }[];
        tabela = abas[0]?.data ?? [];
      } else {
        const texto = decodificarArquivo(await arquivo.arrayBuffer()).replace(/^﻿/, "");
        const brutas = texto.split(/\r?\n/);
        const separador = detectarSeparador(brutas[0] ?? "");
        tabela = brutas.map((l) => dividir(l, separador));
      }
      const categorias = (cad.data?.categorias ?? []).filter((c) => c.natureza !== "receita" && daAreaOk(c.id));
      const leitura = lerPlanilhaProposta(tabela, categorias);
      const comparacao = compararImportacao(linhas, leitura.linhas, (l): Linha => ({
        chave: `planilha-${l.linha}-${Math.random()}`, categoria_id: l.categoria_id, categoria_nome: l.categoria_nome,
        categoria_codigo: l.categoria_codigo, descricao: l.descricao, detalhe: null, pessoa: false, base: zeros(), pedido: l.pedido, aprovado: null,
        observacao: l.observacao, situacao: "rascunho", justificativa: null,
      }));
      setImportacao({ arquivo: arquivo.name, erros: leitura.erros, comparacao });
    } catch {
      setImportacao({ arquivo: arquivo.name, erros: ["Não consegui abrir o arquivo. Use a planilha que o Nexa baixa, em .csv ou .xlsx."], comparacao: { resultado: linhas, mudancas: [], ausentes: 0 } });
    } finally {
      setLendo(false);
    }
  };
  const levar = () => {
    if (!importacao) return;
    setLinhas(importacao.comparacao.resultado);
    setMudou(true);
    setAviso("Planilha levada para a proposta. Confira na tela e salve o rascunho (ou envie).");
    setImportacao(null);
  };

  if (q.isPending) return <p className="text-[13px] text-tinta-fraca">Carregando a proposta…</p>;
  if (q.isError) return <Vazio titulo="Não foi possível carregar a proposta" dica={(q.error as FalhaApi).message} />;
  const v = variacao(totalBase, totalPedido);

  return (
    <div className="grid gap-4">
      <div className="flex flex-wrap items-center gap-3 rounded-xl border border-linha bg-superficie px-4 py-3 text-[13px]">
        <span className={`rounded px-2 py-0.5 text-[12px] font-semibold ${COR_STATUS_AREA[area.status]}`}>{ROTULO_STATUS_AREA[area.status]}</span>
        <span className="text-tinta-media">{area.centro_custo_codigo} · {area.centro_custo_nome}</span>
        {area.aprovadas > 0 && area.aprovadas < area.linhas && <span className="text-tinta-fraca">{area.aprovadas} de {area.linhas} linhas aprovadas</span>}
        <span className="ml-auto num">{ano - 1}: <b>{moeda(totalBase.toString())}</b></span>
        <span className="num">{ano}: <b>{moeda(totalPedido.toString())}</b>
          {v !== null && <span className={v > 0 ? " text-atencao-tinta" : " text-ok-tinta"}> ({v > 0 ? "+" : ""}{percentual(v)})</span>}</span>
        <span className="flex gap-1.5">
          <button type="button" onClick={baixar} className={CLASSE_BOTAO_BARRA} title="A proposta como está na tela, em CSV (abre no Excel)">Baixar planilha</button>
          {algoParaEnviar && (
            <button type="button" disabled={lendo || ocupado || !cad.data} onClick={() => entrada.current?.click()} className={CLASSE_BOTAO_BARRA}
              title="Preencha a planilha baixada e importe de volta: a tela mostra o que muda antes de levar para a proposta">
              {lendo ? "Lendo…" : "Importar planilha"}
            </button>
          )}
          <input ref={entrada} type="file" accept=".csv,.xlsx,text/csv" className="hidden"
            onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) void importar(f); }} />
        </span>
      </div>

      {importacao && (
        <PreviaImportacao importacao={importacao} onLevar={levar} onFechar={() => setImportacao(null)} />
      )}

      <CaixaGrade>
        <table className="w-full border-separate border-spacing-0 text-[12px]">
          {/* De/para mês a mês (pedido do Financeiro de 28/10, item 2): o realizado
              do ano base ao lado do que se pede para o ano, para manter ou mudar. */}
          <thead className="sticky top-0 z-10 bg-superficie-2 text-left text-[11px] font-semibold tracking-wide text-tinta-fraca">
            <tr>
              <th rowSpan={2} className="sticky left-0 z-20 min-w-[230px] border-b border-linha bg-superficie-2 px-3 py-2 align-bottom">Categoria</th>
              {MESES.map((m) => (
                <th key={m} colSpan={2} className="border-b border-l border-linha px-1.5 pt-2 pb-0.5 text-center">{m}</th>
              ))}
              <th colSpan={2} className="border-b border-l border-linha px-3 pt-2 pb-0.5 text-center">Total</th>
              <th rowSpan={2} className="border-b border-linha px-3 py-2 text-right align-bottom">vs {ano - 1}</th>
            </tr>
            <tr className="text-[10px]">
              {[...MESES, "total"].map((m) => (
                <Fragment key={m}>
                  <th className="border-b border-l border-linha px-1.5 pb-1.5 text-right font-medium"
                    title={`Realizado de ${ano - 1}; os meses que ainda não fecharam vêm pela média dos que fecharam`}>{ano - 1}</th>
                  <th className="border-b border-linha px-1.5 pb-1.5 text-right">{ano}</th>
                </Fragment>
              ))}
            </tr>
          </thead>
          <tbody>
            {grupos.map(([catId, g]) => (
              <Grupo key={catId} ano={ano} nome={`${g.codigo ? `${g.codigo} ` : ""}${g.nome}`} categoria={g.categoria}
                detalhes={g.detalhes} itens={g.itens}
                onMudar={atualizar} onTirar={tirar}
                onItemNovo={(descricao, pedido) => novaLinha(catId, descricao, pedido)} />
            ))}
          </tbody>
        </table>
      </CaixaGrade>

      {algoParaEnviar && (
        <div className="flex flex-wrap items-center gap-2 text-[12.5px]">
          <span className="text-tinta-fraca">Acrescentar categoria:</span>
          <select value="" onChange={(e) => e.target.value && novaLinha(e.target.value, null, zeros())}
            className="max-w-[360px] rounded-lg border border-linha bg-superficie px-2 py-1">
            <option value="">Escolha…</option>
            {opcoes.map((c) => <option key={c.id} value={c.id}>{c.codigo ? `${c.codigo} ` : ""}{c.nome}</option>)}
          </select>
          <span className="text-tinta-fraca">Sem gasto em {ano - 1}, ela conta como acima e pede explicação.</span>
        </div>
      )}

      {faltaExplicar.length > 0 && (
        <p className="rounded-lg border border-atencao-linha bg-atencao-bg px-3 py-2 text-[12.5px] text-atencao-tinta">
          Para enviar, explique o aumento de: {faltaExplicar.map((l) => l.categoria_nome).join(", ")}.
        </p>
      )}
      {erro && <p className="rounded-lg border border-risco-linha bg-risco-bg px-3 py-2 text-[12.5px] text-risco-tinta">{erro.message}</p>}
      {aviso && !erro && <p className="text-[12.5px] text-ok-tinta">{aviso}</p>}

      {algoParaEnviar && (
        <div className="flex justify-end gap-2">
          <button type="button" disabled={!mudou || ocupado} onClick={() => { setAviso(null); salvar.mutate(); }}
            className={CLASSE_BOTAO_BARRA}>{salvar.isPending ? "Salvando…" : "Salvar rascunho"}</button>
          <button type="button" disabled={faltaExplicar.length > 0 || ocupado}
            onClick={() => { setAviso(null); enviar.mutate(); }}
            className="rounded-lg bg-laranja px-4 py-2 text-[13px] font-bold text-azul disabled:cursor-not-allowed disabled:opacity-45">
            {enviar.isPending ? "Enviando…" : "Enviar ao Financeiro"}
          </button>
        </div>
      )}
    </div>
  );
}

/** As colunas da grade: a categoria, dois por mês, os dois totais e a variação. */
const COLUNAS = 1 + MESES.length * 2 + 2 + 1;

/**
 * Uma categoria: o ano base ao lado do pedido, os itens novos e a explicação.
 * Com pessoas ou subcategorias (082), a linha da categoria mostra o total
 * somado e abre o detalhe, onde se edita cada um; a linha que sobra é o que
 * não é de ninguém.
 */
function Grupo({ ano, nome, categoria, detalhes, itens, onMudar, onTirar, onItemNovo }: {
  ano: number;
  nome: string;
  categoria: Linha | null;
  detalhes: Linha[];
  itens: Linha[];
  onMudar: (chave: string, m: Partial<Linha>) => void;
  onTirar: (l: Linha) => void;
  onItemNovo: (descricao: string, pedido: string[]) => void;
}) {
  const [novoItem, setNovoItem] = useState(false);
  const [aberta, setAberta] = useState(false);
  const podeItem = categoria ? editavel(categoria.situacao) || itens.some((i) => editavel(i.situacao)) : true;
  const totalCat = somaCategoria(categoria, detalhes);
  const porPessoa = detalhes.some((d) => d.pessoa);
  const TD = "border-b border-linha px-1.5 py-1 text-right";
  const rotulo = "sticky left-0 z-[1] border-b border-linha bg-superficie px-3 py-1";
  return (
    <>
      {detalhes.length > 0 ? (
        <>
          <LinhaTotal ano={ano} nome={nome} base={totalCat.base} pedido={totalCat.pedido} TD={TD} rotulo={rotulo}
            aberta={aberta} onAlternar={() => setAberta((a) => !a)} quantos={detalhes.length} porPessoa={porPessoa} />
          {aberta && (
            <>
              {detalhes.map((d) => <LinhaValores key={d.chave} ano={ano} linha={d} rotulo={rotulo} TD={TD} onMudar={onMudar} onTirar={onTirar} />)}
              {categoria && (
                <LinhaValores ano={ano} linha={categoria} rotulo={rotulo} TD={TD} onMudar={onMudar} onTirar={onTirar}
                  resto={porPessoa ? "Sem pessoa ligada" : "Sem subcategoria"} />
              )}
            </>
          )}
        </>
      ) : categoria
        ? <LinhaValores ano={ano} linha={categoria} nome={nome} rotulo={rotulo} TD={TD} onMudar={onMudar} onTirar={onTirar} />
        : (
          <tr>
            <td className={`${rotulo} pt-3 font-semibold`} title={nome}><span className="block max-w-[260px] truncate">{nome}</span></td>
            <td colSpan={COLUNAS - 1} className="border-b border-linha" />
          </tr>
        )}
      {itens.map((it) => <LinhaValores key={it.chave} ano={ano} linha={it} rotulo={rotulo} TD={TD} onMudar={onMudar} onTirar={onTirar} />)}
      <tr>
        <td colSpan={COLUNAS} className="border-b border-linha px-3 pb-3">
          {categoria && <Explicacao linha={categoria} base={totalCat.base} pedido={totalCat.pedido} onMudar={onMudar} />}
          {podeItem && (novoItem
            ? <ItemNovo ano={ano} onCancelar={() => setNovoItem(false)} onCriar={(d, p) => { onItemNovo(d, p); setNovoItem(false); }} />
            : <button type="button" onClick={() => setNovoItem(true)} className="mt-1 text-[11.5px] font-semibold text-azul-medio hover:underline">
                + item novo (uma contratação, por exemplo)
              </button>)}
        </td>
      </tr>
    </>
  );
}

/** O total da categoria com detalhe: só leitura, e abre o detalhe. */
function LinhaTotal({ ano, nome, base, pedido, TD, rotulo, aberta, onAlternar, quantos, porPessoa }: {
  ano: number;
  nome: string;
  base: string[];
  pedido: string[];
  TD: string;
  rotulo: string;
  aberta: boolean;
  onAlternar: () => void;
  quantos: number;
  porPessoa: boolean;
}) {
  const acima = new Set(mesesAcima(base, pedido));
  const total = soma(pedido), tBase = soma(base);
  const v = variacao(tBase, total);
  return (
    <tr>
      <td className={`${rotulo} pt-3`} title={nome}>
        <button type="button" onClick={onAlternar} aria-expanded={aberta} className="flex max-w-[260px] items-center gap-1 text-left font-semibold">
          <span aria-hidden className="text-tinta-fraca">{aberta ? "▾" : "▸"}</span>
          <span className="truncate">{nome}</span>
        </button>
        <span className="ml-4 text-[10.5px] text-tinta-fraca">
          {aberta ? "fechar" : `abrir ${quantos} ${porPessoa ? (quantos === 1 ? "pessoa" : "pessoas") : (quantos === 1 ? "subcategoria" : "subcategorias")}`}
        </span>
      </td>
      {pedido.map((val, i) => (
        <Fragment key={i}>
          <td className={`${TD} num border-l pt-3 text-tinta-fraca`}>{curto(base[i]!)}</td>
          <td className={`${TD} num pt-3 font-semibold ${acima.has(i + 1) ? "bg-atencao-bg" : ""}`}>{curto(val)}</td>
        </Fragment>
      ))}
      <td className={`${TD} num border-l pt-3 text-tinta-fraca`}>{curto(tBase.toString())}</td>
      <td className={`${TD} num pt-3 font-semibold`}>{curto(total.toString())}</td>
      <td className={`${TD} num pt-3 ${v !== null && v > 0 ? "text-atencao-tinta" : "text-tinta-fraca"}`}>
        {v === null ? (total > 0n ? "novo" : "—") : `${v > 0 ? "+" : ""}${percentual(v, 0)}`}
        <span className="sr-only">{ano}</span>
      </td>
    </tr>
  );
}

function LinhaValores({ ano, linha, nome, resto, rotulo, TD, onMudar, onTirar }: {
  ano: number;
  linha: Linha;
  /** O nome da categoria, na linha dela; o item novo tem a própria descrição. */
  nome?: string;
  /** A linha da categoria que, com detalhe, guarda o que não é de ninguém. */
  resto?: string;
  rotulo: string;
  TD: string;
  onMudar: (chave: string, m: Partial<Linha>) => void;
  onTirar: (l: Linha) => void;
}) {
  const ed = editavel(linha.situacao);
  const acima = new Set(mesesAcima(linha.base, linha.pedido));
  const total = soma(linha.pedido);
  const base = soma(linha.base);
  const v = variacao(base, total);
  const item = linha.descricao !== null;
  // A pessoa, a subcategoria e o resto são linhas de dentro da categoria: sem
  // o espaço da linha da categoria, e o nome recuado.
  const dentro = item || linha.detalhe !== null || !!resto;
  return (
    <>
      <tr>
        <td className={`${rotulo} ${dentro ? "" : "pt-3"}`} title={nome ?? linha.detalhe ?? resto}>
          {!item && nome && <span className="block max-w-[260px] truncate font-semibold">{nome}</span>}
          {linha.detalhe !== null && <span className="block max-w-[260px] truncate text-[12px]">↳ {linha.detalhe}</span>}
          {resto && <span className="block max-w-[260px] truncate text-[12px] text-tinta-media italic">↳ {resto}</span>}
          {item ? (
            ed ? <input value={linha.descricao ?? ""} onChange={(e) => onMudar(linha.chave, { descricao: e.target.value })}
                   placeholder="O que é (ex.: contratação de analista)" aria-label="Descrição do item novo"
                   className="w-full rounded border border-linha bg-superficie px-2 py-0.5 text-[12px]" />
               : <span className="text-[12px]">↳ {linha.descricao}</span>
          ) : null}
          <span className="ml-1 text-[10.5px] text-tinta-fraca">{ROTULO_SITUACAO[linha.situacao]}</span>
          {item && ed && <button type="button" onClick={() => onTirar(linha)} className="ml-2 text-[10.5px] text-risco-tinta hover:underline">tirar</button>}
        </td>
        {linha.pedido.map((val, i) => (
          <Fragment key={i}>
            <td className={`${TD} num border-l text-tinta-fraca ${dentro ? "" : "pt-3"}`}>{item ? "" : curto(linha.base[i]!)}</td>
            <td className={`${TD} ${dentro ? "" : "pt-3"} ${acima.has(i + 1) && !item ? "bg-atencao-bg" : ""}`}>
              {ed ? <Celula valor={val} onMudar={(c) => onMudar(linha.chave, { pedido: linha.pedido.map((x, j) => (j === i ? c : x)) })} />
                  : <span className="num">{curto(val)}</span>}
            </td>
          </Fragment>
        ))}
        <td className={`${TD} num border-l text-tinta-fraca ${dentro ? "" : "pt-3"}`}>{item ? "" : curto(base.toString())}</td>
        <td className={`${TD} num font-semibold ${dentro ? "" : "pt-3"}`}>{curto(total.toString())}</td>
        <td className={`${TD} num ${dentro ? "" : "pt-3"} ${v !== null && v > 0 ? "text-atencao-tinta" : "text-tinta-fraca"}`}>
          {item ? "novo" : v === null ? (total > 0n ? "novo" : "—") : `${v > 0 ? "+" : ""}${percentual(v, 0)}`}
        </td>
      </tr>
      {linha.aprovado && linha.situacao === "ajustada" && (
        <tr className="text-ok-tinta">
          <td className={rotulo}><span className="text-[11px] font-semibold">Aprovado pelo Financeiro</span></td>
          {linha.aprovado.map((val, i) => (
            <Fragment key={i}><td className={`${TD} border-l`} /><td className={`${TD} num`}>{curto(val)}</td></Fragment>
          ))}
          <td className={`${TD} border-l`} />
          <td className={`${TD} num font-semibold`}>{curto(soma(linha.aprovado).toString())}</td>
          <td className={TD} />
        </tr>
      )}
      {linha.justificativa && (linha.situacao === "devolvida" || linha.situacao === "ajustada") && (
        <tr>
          <td colSpan={COLUNAS} className={`border-b border-linha px-3 py-1 text-[11.5px] ${linha.situacao === "devolvida" ? "text-risco-tinta" : "text-ok-tinta"}`}>
            Financeiro: {linha.justificativa}
          </td>
        </tr>
      )}
    </>
  );
}

function Explicacao({ linha, base, pedido, onMudar }: {
  linha: Linha;
  /** A categoria somada (a linha dela e os detalhes): é por ela que se pede a explicação. */
  base: string[];
  pedido: string[];
  onMudar: (chave: string, m: Partial<Linha>) => void;
}) {
  const precisa = pedeExplicacao(base, pedido);
  if (!editavel(linha.situacao)) {
    return linha.observacao ? <p className="mt-1 text-[11.5px] text-tinta-media">Explicação do gestor: {linha.observacao}</p> : null;
  }
  if (!precisa) return null;
  const meses = mesesAcima(base, pedido);
  return (
    <label className="mt-1 block">
      <span className="block text-[11.5px] font-semibold text-atencao-tinta">
        Por que aumenta?{meses.length > 0 ? ` Acima em ${textoMesesAcima(meses)}.` : " O ano passou."} *
      </span>
      <textarea value={linha.observacao} onChange={(e) => onMudar(linha.chave, { observacao: e.target.value })} rows={2}
        className={`mt-1 block w-full max-w-3xl rounded-lg border bg-superficie px-2 py-1 text-[12.5px] ${linha.observacao.trim() ? "border-linha" : "border-atencao-linha"}`} />
    </label>
  );
}

function ItemNovo({ ano, onCancelar, onCriar }: { ano: number; onCancelar: () => void; onCriar: (descricao: string, pedido: string[]) => void }) {
  const [descricao, setDescricao] = useState("");
  const [valor, setValor] = useState("");
  const [mes, setMes] = useState(1);
  const centavos = paraCentavos(valor);
  return (
    <div className="mt-1 flex flex-wrap items-end gap-2 rounded-lg bg-superficie-2 px-3 py-2 text-[12px]">
      <label>O que é<input value={descricao} onChange={(e) => setDescricao(e.target.value)} placeholder="Contratação de analista"
        className="mt-0.5 block w-56 rounded border border-linha bg-superficie px-2 py-1" /></label>
      <label>Valor por mês (R$)<input value={valor} onChange={(e) => setValor(e.target.value)} inputMode="decimal"
        className="num mt-0.5 block w-28 rounded border border-linha bg-superficie px-2 py-1 text-right" /></label>
      <label>A partir de<select value={mes} onChange={(e) => setMes(Number(e.target.value))}
        className="mt-0.5 block rounded border border-linha bg-superficie px-2 py-1">
        {MESES.map((m, i) => <option key={m} value={i + 1}>{m}/{String(ano).slice(2)}</option>)}
      </select></label>
      <button type="button" disabled={!descricao.trim() || !centavos} onClick={() => onCriar(descricao.trim(), aPartirDe(centavos!, mes))}
        className="rounded-lg bg-laranja px-3 py-1 font-bold text-azul disabled:opacity-45">Incluir</button>
      <button type="button" onClick={onCancelar} className="text-tinta-fraca">cancelar</button>
    </div>
  );
}

/** Célula de valor: digita em reais, guarda centavos. Reformata só ao sair, para não brigar com a digitação. */
export function Celula({ valor, onMudar }: { valor: string; onMudar: (centavos: string) => void }) {
  const [texto, setTexto] = useState(deCentavos(valor));
  const [foco, setFoco] = useState(false);
  useEffect(() => { if (!foco) setTexto(deCentavos(valor)); }, [valor, foco]);
  return (
    <input value={texto} inputMode="decimal" onFocus={() => setFoco(true)}
      onChange={(e) => setTexto(e.target.value)}
      onBlur={() => { setFoco(false); onMudar(paraCentavos(texto) ?? "0"); }}
      className="num w-[78px] rounded border border-linha bg-transparent px-1 py-0.5 text-right text-[12px] focus:border-laranja focus:bg-superficie" />
  );
}

/** Valor sem centavos na grade: doze colunas não cabem com "R$ 1.234,56". */
export function curto(centavos: string): string {
  const reais = Math.round(Number(centavos) / 100);
  return reais.toLocaleString("pt-BR");
}


const ROTULO_MUDANCA = {
  alterada: "muda",
  nova_categoria: "categoria nova",
  novo_item: "item novo",
  travada: "já enviada ou aprovada: fica como está",
  sem_par: "não está na proposta: pessoa e subcategoria vêm do ano base, a planilha não cria",
} as const;

/** O que a planilha muda, antes de levar para a proposta. Com erro, nada é levado. */
function PreviaImportacao({ importacao, onLevar, onFechar }: {
  importacao: { arquivo: string; erros: string[]; comparacao: Comparacao<Linha> };
  onLevar: () => void;
  onFechar: () => void;
}) {
  const { erros, comparacao } = importacao;
  const levaveis = comparacao.mudancas.filter((m) => m.tipo !== "travada" && m.tipo !== "sem_par");
  const travadas = comparacao.mudancas.length - levaveis.length;
  return (
    <Modal titulo={`Importar planilha · ${importacao.arquivo}`} onFechar={onFechar} largura="max-w-2xl">
      {erros.length > 0 ? (
        <div className="grid gap-2 text-[13px]">
          <p className="rounded-lg border border-risco-linha bg-risco-bg px-3 py-2 text-risco-tinta">
            A planilha tem {erros.length === 1 ? "um problema" : `${erros.length} problemas`}. Corrija e importe de novo: nada foi levado para a proposta.
          </p>
          <ul className="thin-scroll grid max-h-[320px] gap-1 overflow-y-auto text-[12.5px] text-tinta-media">
            {erros.slice(0, 30).map((e) => <li key={e}>· {e}</li>)}
          </ul>
        </div>
      ) : (
        <div className="grid gap-3 text-[13px]">
          {levaveis.length === 0 ? (
            <p className="text-tinta-media">A planilha é igual à proposta: não há o que levar.</p>
          ) : (
            <div className="thin-scroll max-h-[340px] overflow-y-auto">
              <table className="w-full text-[12.5px]">
                <thead className="sticky top-0 bg-superficie text-left text-[11px] text-tinta-fraca">
                  <tr><th className="pb-1.5">Linha</th><th className="pb-1.5 pl-4 text-right">Antes</th><th className="pb-1.5 pl-4 text-right">Depois</th><th className="pb-1.5 pl-3" /></tr>
                </thead>
                <tbody>
                  {comparacao.mudancas.map((m) => (
                    <tr key={m.rotulo} className="border-t border-linha">
                      <td className="py-1.5 pr-2">{m.rotulo}</td>
                      <td className="num whitespace-nowrap py-1.5 pl-4 text-right">{moeda(m.antes.toString())}</td>
                      <td className="num whitespace-nowrap py-1.5 pl-4 text-right font-semibold">{moeda(m.depois.toString())}</td>
                      <td className={`whitespace-nowrap py-1.5 pl-3 text-[11.5px] ${m.tipo === "travada" ? "text-tinta-fraca" : "text-tinta-media"}`}>{ROTULO_MUDANCA[m.tipo]}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {(travadas > 0 || comparacao.ausentes > 0) && (
            <p className="text-[12px] text-tinta-fraca">
              {travadas > 0 && (travadas === 1 ? "1 linha já enviada ou aprovada fica como está. " : `${travadas} linhas já enviadas ou aprovadas ficam como estão. `)}
              {comparacao.ausentes > 0 && (comparacao.ausentes === 1 ? "1 linha da proposta não estava na planilha e fica como está." : `${comparacao.ausentes} linhas da proposta não estavam na planilha e ficam como estão.`)}
            </p>
          )}
          <p className="text-[12px] text-tinta-fraca">Levar para a proposta não grava: confira na tela e salve o rascunho.</p>
        </div>
      )}
      <div className="mt-4 flex justify-end gap-2">
        <button type="button" onClick={onFechar} className={CLASSE_BOTAO_BARRA}>Voltar</button>
        {erros.length === 0 && levaveis.length > 0 && (
          <button type="button" onClick={onLevar} className="rounded-lg bg-laranja px-4 py-2 text-[13px] font-bold text-azul">Levar para a proposta</button>
        )}
      </div>
    </Modal>
  );
}
